import React from 'react';
import propTypes from 'prop-types';

import { navBtnPropTypes } from '../Detail/DetailPropTypes';
import { residencePropTypesData } from '../../utils/DrupalEntityTypes/Residence/ResidencePropTypes';
import useDetailViewTracking from '../../hooks/useDetailViewTracking';

import FadingContainer from '../FadingContainer/FadingContainer';
import SystemMessage from '../SystemMessage/SystemMessage';
import DetailTable from '../Detail/DetailTable';
import FloorPlanDetailCore from '../FloorPlan/FloorPlanDetailCore';
import useResidenceDetail from './useResidenceDetail';
import PibResidenceHeader from './PibResidenceHeader';
import PibResidenceMapSection from './PibResidenceMapSection';

/**
 * PibResidenceDetail - Residence detail for the PIB page.
 * Includes internal header and embedded map section.
 */
const PibResidenceDetail = ({
  data,
  uuid,
  navBtns,
  navClass,
  tracking,
}) => {
  useDetailViewTracking({
    nid: data?.nid,
    tracking,
  });

  // Get shared residence detail data from hook
  const {
    unitData,
    isDataLoaded,
    title,
    description,
    svgId,
    hasFees,
    tableInfo,
    detailDialogData,
    pricingTriggered,
    setPricingTriggered,
    platformShowPricing,
    platformShowPricingInitially,
  } = useResidenceDetail(uuid, data);

  return (
    <FadingContainer>
      <div data-testid="residence-detail">
        <SystemMessage
          status={detailDialogData.status || 'info'}
          message={detailDialogData.message}
          fading
        />

        <PibResidenceHeader
          unitData={unitData}
          title={title}
          isDataLoaded={isDataLoaded}
          navBtns={navBtns}
          navClass={navClass}
          hasFees={hasFees}
          pricingTriggered={pricingTriggered}
          platformShowPricing={platformShowPricing}
          platformShowPricingInitially={platformShowPricingInitially}
          setPricingTriggered={setPricingTriggered}
        />

        <DetailTable
          id="info-scroll"
          listData={tableInfo}
          tableDescription={description}
          className="detailSection"
        />

        <FloorPlanDetailCore
          data={unitData}
          minimalData={data}
          isLoadingPhotoGallery={!unitData}
          isLoadingVideo={!unitData}
          isLoadingVirtualTour={!unitData}
          parent="page"
          contentType="residence"
        />

        {unitData && svgId ? (
          <PibResidenceMapSection svgId={svgId} />
        ) : null}
      </div>
    </FadingContainer>
  );
};

PibResidenceDetail.propTypes = {
  uuid: propTypes.string,
  data: residencePropTypesData,
  navBtns: navBtnPropTypes,
  navClass: propTypes.string,
  tracking: propTypes.shape({
    endpoint: propTypes.string,
    token: propTypes.string,
    identity: propTypes.shape({
      residentNid: propTypes.number,
      contactHash: propTypes.string,
      communityNid: propTypes.number,
    }),
  }),
};

PibResidenceDetail.defaultProps = {
  uuid: null,
  data: null,
  navBtns: null,
  navClass: null,
  tracking: null,
};

export default PibResidenceDetail;
